import { ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { LocalStorageService } from 'src/app/local-storage.service';
import { Page } from 'src/app/page';
import { environment } from 'src/environments/environment';
import { SubjectDocumentsDialogComponent } from '../subject-documents/subject-documents-dialog/subject-documents-dialog.component';
import { ModalEventEnum, ModalSizeEnum } from 'src/types';
import { MatDialog } from '@angular/material/dialog';
import { CustomPaginationService } from 'src/services/custom-pagination.service';
import { DatePipe } from '@angular/common';
import { AppService } from 'src/services/app.service';
import { MsWordService } from 'src/services/ms-word.service';
import { ServiceProviderSubjectService } from 'src/services/service-provider-subject.service';
import { PreviewDocumentComponent } from '../preview-document/preview-document.component';
import { Document } from 'src/models/document.model';
import { OciService } from 'src/services/oci.service';

interface DocumentListItem {
  documentId: string;
  name: string;
}
@Component({
  selector: 'service-provider-subject-documents',
  templateUrl: './service-provider-subject-documents.component.html',
  styleUrls: ['./service-provider-subject-documents.component.scss']
})
export class ServiceProviderSubjectDocumentsComponent implements OnInit {
  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
  @ViewChild('file') file: ElementRef<HTMLInputElement>;
  @Input() addBtnDisabled;

  filterForm: FormGroup;
  appliedTextFilters: string[] = [];
  appliedFilters: {
    displayValue: string;
    value: object | string;
    key: 'minDate' | 'maxDate';
  }[] = [];

  filterValue = {
    minDate: '',
    maxDate: '',
    text: [],
  };

  badgeCount: number;
  isLoading = true;
  addButtonDisabled = false;
  createDocumentOption = true;
  enableEditing = false;

  subjectId: string;
  page: Page<Document> = new Page();
  maxDate: Date = new Date(2999, 11, 31);

  documentOptions: DocumentListItem[] = [];
  filteredDocumentOptions: Observable<any[]>;

  fileToUpload: File = null;
  loggedIn: any;
  serviceProviderId;

  object: any = {
    document: '',
    action: '',
    subjectId: '',
    message: '',
    title: '',
  };

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private serviceProviderSubjectService: ServiceProviderSubjectService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private paginationService: CustomPaginationService,
    private datepipe: DatePipe,
    private localStorageService: LocalStorageService,
    private appService: AppService,
    private msWordService: MsWordService,
    private ociService: OciService
  ) {
    this.filterForm = this.formBuilder.group({
      searchDocuments: [],
      minDate: [],
      maxDate: [],
    });

    this.loggedIn = JSON.parse(this.localStorageService.get('loggedUser'));
    this.serviceProviderId = this.loggedIn?.serviceProviderId;
  }

  ngOnInit(): void {
    this.getDocumentsForSubject(this.filterValue);
  }

  getDocumentsForSubject(filter: any = this.filterValue): void {
    const [, id] = this.router.url.split('/subjects/service-providers/');
    this.subjectId = id;

    if (!this.subjectId || !this.serviceProviderId) {
      return;
    }
    this.isLoading = true;

    this.ociService.getDocumentsForServiceProviderSubject(this.page.pageable, filter, this.subjectId, this.serviceProviderId).subscribe({
      next: (page: any) => {
        this.page = page;
        this.page.pageable.pagePerShow = page.number + 1;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => (this.isLoading = false)
    });

    this.serviceProviderSubjectService.getServiceProviderSubjectStatus(this.subjectId, this.serviceProviderId).subscribe(result => {
      this.addButtonDisabled = result !== "ACTIVE";
    });
  }

  getNextPage() {
    this.page.pageable = this.paginationService.getNextPage(this.page);
    this.getDocumentsForSubject(this.filterValue);
  }

  getPreviousPage() {
    this.page.pageable = this.paginationService.getPreviousPage(this.page);
    this.getDocumentsForSubject(this.filterValue);
  }

  getFirstPage() {
    this.page.pageable = this.paginationService.getFirstPage(this.page);
    this.getDocumentsForSubject(this.filterValue);
  }

  getLastPage() {
    this.page.pageable = this.paginationService.getLastPage(this.page);
    this.getDocumentsForSubject(this.filterValue);
  }

  getPageInNewSize(size: number) {
    this.page.pageable = this.paginationService.getPageInNewSize(this.page, size);
    this.getDocumentsForSubject(this.filterValue);
  }

  applyFilter() {
    this.filterValue.minDate = this.filterForm.value.minDate
      ? this.datepipe.transform(this.filterForm.value.minDate, 'yyyy-MM-dd')
      : '';

    this.filterValue.maxDate = this.filterForm.value.maxDate
      ? this.datepipe.transform(this.filterForm.value.maxDate, 'yyyy-MM-dd')
      : '';

    const text = this.filterForm.value.searchDocuments?.trim();
    if (text) {
      this.filterValue.text.push(text);
      this.appliedTextFilters.push(text);
      this.filterForm.get('searchDocuments').reset();
    }

    this.getFirstPage();
  }

  removeFilter(filter: { displayValue: string; value: object | string; key: 'minDate' | 'maxDate' }): void {
    if (filter.key === 'minDate') {
      this.filterValue.minDate = '';
      this.filterForm.get('minDate')?.reset();
    }
  
    if (filter.key === 'maxDate') {
      this.filterValue.maxDate = '';
      this.filterForm.get('maxDate')?.reset();
    }
  
    this.appliedFilters = this.appliedFilters.filter(f => f !== filter);
    this.badgeCount = this.appliedFilters.length;
  
    this.getFirstPage();
  }

  removeTextFilter(text: string) {
    this.filterValue.text = this.filterValue.text.filter(t => t !== text);
    this.appliedTextFilters = this.appliedTextFilters.filter(t => t !== text);
    this.getFirstPage();
  }

  resetFilter() {
    this.filterValue = { minDate: '', maxDate: '', text: [] };
  
    this.appliedTextFilters = [];
    this.appliedFilters = [];

    this.filterForm.get('minDate')?.reset();
    this.filterForm.get('maxDate')?.reset();
    this.filterForm.get('searchDocuments')?.reset();
  
    this.getFirstPage();
  }

  handleFileInput(files: FileList) {
    this.fileToUpload = files.item(0);

    const dialogRef = this.dialog.open(SubjectDocumentsDialogComponent, {
      panelClass: 'overlay-panel',
      width: ModalSizeEnum.DEFAULT,
    });

    dialogRef.afterClosed().subscribe(({ event, ...rest }) => {
      if (event === 'new') {
        this.ociService.uploadFileForSubject(this.fileToUpload, this.subjectId, rest.data.value)
        .subscribe(() => {
          this.getDocumentsForSubject(this.filterValue);
          this.file.nativeElement.value = null;
        })
      }
    });
  }

  openDialog(action: 'delete' | 'view' | 'cancel' | 'send' | 'download' | 'print' | 'viewInNewTab' | 'edit', obj: any) {
    this.object.document = obj;
    this.object.action = action;
    this.object.subjectId = this.subjectId;
    if (action === ModalEventEnum.VIEW) {
      const dialogRef = this.dialog.open(PreviewDocumentComponent, {
        panelClass: 'overlay-panel',
        disableClose: true,
        data: this.object,
      });
      dialogRef.afterClosed().subscribe(() => {});
    } else if (action === ModalEventEnum.DOWNLOAD) {
      this.ociService.downloadDocument(obj.documentId, obj.name, obj.format);
    } else if (action === ModalEventEnum.PRINT) {
      this.ociService.printDocument(obj.documentId, obj.name);
    } else if (action === ModalEventEnum.VIEW_IN_NEW_TAB) {
      const docUrl = this.appService.getDocManagerUrl() + environment.ociViewDocument + this.object.document.documentId;
      window.open(docUrl, '_blank');
    } else if (action === ModalEventEnum.EDIT) {
      const body = {
        documentId: obj.documentId,
        fileName: obj.name,
      };
      this.msWordService.openWordDocumentForSubject(body, this.loggedIn, this.subjectId);
    }
  }

  createDocumentWithWord(option: DocumentListItem): void {
    const dialogRef = this.dialog.open(SubjectDocumentsDialogComponent, {
      panelClass: 'overlay-panel',
      width: ModalSizeEnum.DEFAULT,
    });
  
    dialogRef.afterClosed().subscribe(({ event, ...rest }) => {
      if (event === 'new') {
        const body = {
          documentId: option.documentId,
          fileName: option.name,
        };
  
        this.msWordService.openWordDocumentForSubject(body, this.loggedIn, this.subjectId);
      } 
    });
  }

}